import React from 'react';
import { DatePicker, Dropdown, IDropdownOption, SearchBox, Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowRight,
    faCalendarCheck, 
    faCalendarDays, 
    faClock, 
    faExclamationTriangle, 
    faList, 
    faSpinner, 
    faTableColumns,
    faTruckFast
} from '@fortawesome/pro-light-svg-icons';
import moment from 'moment';
import styles from '../styles.module.scss';
import { TOperationStatusFilter, TOperationTypeFilter, TOperationViewMode } from '../types';
import GeneralService from '../../../../services/general';
import Label from '../../../typography/label';
import Text from '../../../typography/text';

interface IWorkspaceHeaderProps {
    counts: {
        total: number;
        overdue: number;
        inProgress: number;
        completed: number;
    };
    selectedDate: {
        startDate: string;
        endDate: string;
    };
    onDateChange(startDate: string, endDate: string): void;
    
    statusFilter: TOperationStatusFilter;
    onStatusFilterChange(filter: TOperationStatusFilter): void;
    
    typeFilter: TOperationTypeFilter;
    onTypeFilterChange(filter: TOperationTypeFilter): void;
    
    searchKeyword: string;
    onSearchChange(keyword: string): void;
    
    viewMode: TOperationViewMode;
    onViewModeChange(mode: TOperationViewMode): void;
    
    onRefresh(): void;
}

const TYPE_OPTIONS: IDropdownOption[] = [
    { key: 'all', text: 'Semua Tipe Aktivitas' },
    { key: 'ship', text: '🚢 Jadwal Kapal (Pelayaran)' },
    { key: 'loading', text: '📦 Muat Depo (Loading)' },
    { key: 'unloading', text: '🏗️ Bongkar (Unloading)' },
    { key: 'dooring', text: '🚚 Dooring / Trucking' },
];

const WorkspaceHeader: React.FC<IWorkspaceHeaderProps> = ({
    counts,
    selectedDate,
    onDateChange,
    statusFilter,
    onStatusFilterChange,
    typeFilter,
    onTypeFilterChange,
    searchKeyword,
    onSearchChange,
    viewMode,
    onViewModeChange,
    onRefresh,
}) => {
    return (
        <div className={styles.headerContainer}>
            {/* KPI Metrics Summary Bar */}
            <div className={styles.kpiGrid}>
                {/* Total */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'all' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('all')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Total Aktivitas</span>
                        <span className={styles.kpiValue}>{counts.total}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#f1f5f9', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                </div>

                {/* Overdue */}
                <div 
                    className={`${styles.kpiCard} ${styles.kpiOverdue} ${statusFilter === 'overdue' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('overdue')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel} style={{ color: '#b91c1c' }}>Overdue / Lewat Jadwal</span>
                        <span className={styles.kpiValue} style={{ color: '#dc2626' }}>{counts.overdue}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#fee2e2', color: '#dc2626' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                </div>

                {/* In Progress */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'in_progress' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('in_progress')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Sedang Berjalan</span>
                        <span className={styles.kpiValue} style={{ color: '#0284c7' }}>{counts.inProgress}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#e0f2fe', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faSpinner} />
                    </div>
                </div>

                {/* Completed */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'completed' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('completed')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Selesai</span>
                        <span className={styles.kpiValue} style={{ color: '#16a34a' }}>{counts.completed}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#dcfce7', color: '#16a34a' }}>
                        <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                </div>
            </div>

            {/* Controls Bar: Date Navigation, Search, Filters, View Switcher */}
            <div className={styles.controlsBar}>
                {/* Date Range Navigation */}
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <div className={styles.dateRangeContainer}>
                        <span className={styles.dateLabel}>Periode:</span>
                        <DatePicker
                            placeholder="Dari tanggal..."
                            value={moment(selectedDate.startDate).toDate()}
                            onSelectDate={(date) => {
                                if (date) {
                                    const m = moment(date).startOf('day');
                                    let endM = moment(selectedDate.endDate);
                                    if (endM.isBefore(m)) {
                                        endM = m.clone().endOf('day');
                                    }
                                    onDateChange(m.toISOString(), endM.toISOString());
                                }
                            }}
                            formatDate={GeneralService.formatDate}
                            styles={{ root: { width: 135 } }}
                        />
                        <div className={styles.dateRangeSeparator}>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                        <DatePicker
                            placeholder="Sampai tanggal..."
                            value={moment(selectedDate.endDate).toDate()}
                            onSelectDate={(date) => {
                                if (date) {
                                    const m = moment(date).endOf('day');
                                    let startM = moment(selectedDate.startDate);
                                    if (m.isBefore(startM)) {
                                        startM = m.clone().startOf('day');
                                    }
                                    onDateChange(startM.toISOString(), m.toISOString());
                                }
                            }}
                            formatDate={GeneralService.formatDate}
                            styles={{ root: { width: 135 } }}
                        />
                    </div>
                </Stack>

                {/* Search & Filters */}
                <div className={styles.filtersGroup}>
                    <div className={styles.searchBoxWrapper}>
                        <SearchBox
                            placeholder="Cari Kapal, Voy, Kontainer, Order..."
                            value={searchKeyword}
                            onChange={(_, val) => onSearchChange(val || '')}
                            onClear={() => onSearchChange('')}
                        />
                    </div>

                    <Dropdown
                        options={TYPE_OPTIONS}
                        selectedKey={typeFilter}
                        onChange={(_, opt) => {
                            if (opt) onTypeFilterChange(opt.key as TOperationTypeFilter);
                        }}
                        styles={{ root: { minWidth: 190 } }}
                    />

                    {/* View Switcher Toggle */}
                    <div className={styles.viewToggleGroup}>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'table' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('table')}
                            title="Operational Table View"
                        >
                            <FontAwesomeIcon icon={faList} /> Tabel
                        </button>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'board' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('board')}
                            title="Board / Card View"
                        >
                            <FontAwesomeIcon icon={faTableColumns} /> Board
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceHeader;
